import {
  Box,
  Modal,
  ButtonWithDropdown,
  Grid,
  Text,
} from '@simplywallst/ui-core';
import SupportSolid from '@simplywallst/ui-core/icons/SupportSolid';
import { useCallback, useState } from 'react';
import { useTracking } from 'react-tracking';
import { useUserLocale } from '../../../../../hooks/useUserLocale';
import { useChangeLocale } from './hooks/useChangeLocale';
import { useLocale } from '@/hooks/useLocale';
import { useHotjar } from '@/hooks/useHotjar';
import { useLocation } from '@/hooks/useLocation';
import { isPageSupported } from '@/utils/locale';
import { useTranslation } from '@/hooks/useTranslation';
import { useI18nFeedback } from '@/hooks/useI18nFeedback';
import useUser from '@/hooks/useUser';

const localeNames = new Map<string, string>([
  ['tr', 'Turkish'],
  ['nl', 'Dutch'],
  ['pt', 'Portuguese'],
  ['zh', 'Chinese'],
  ['kr', 'Korean'],
  ['ja', 'Japanese'],
  ['ru', 'Russian'],
  ['hi', 'Hindi'],
  ['id', 'Indonesian'],
  ['fr', 'French'],
  ['it', 'Italian'],
  ['sv', 'Swedish'],
]);

const localeOptions = [
  { label: 'English', value: 'en' },
  { label: 'Español', value: 'es' },
  { label: 'Deutsch', value: 'de' },
  { label: '한국인', value: 'ko' },
  { label: '日本語', value: 'ja' },
  { label: 'Français', value: 'fr' },
  { label: 'Italiano', value: 'it' },
  { label: 'Nederlands', value: 'nl' },
  { label: 'Türkçe', value: 'tr' },
  { label: 'Svenska', value: 'sv' },
];

export const LanguageButton = () => {
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'language_dropdown',
  });

  const [hotjar] = useHotjar();

  const [, setUserLocale] = useUserLocale();
  const { changeLocale } = useChangeLocale();
  const locale = useLocale();
  const location = useLocation();

  const makeFeedbackDrawer = useI18nFeedback();
  const user = useUser();

  const { type } = location;
  const [chosenLocale, setChosenLocale] = useState<string>('');

  const handleSelection = useCallback(
    async (value: string) => {
      if (value === 'feedback') {
        makeFeedbackDrawer();
        return;
      }
      trackEvent({
        action: 'click',
        locale: value,
      });
      hotjar?.('event', 'locale_change');
      await setUserLocale(value);
      if (isPageSupported(type, value)) {
        changeLocale(value);
      } else {
        setChosenLocale(value);
      }
    },
    [trackEvent, setUserLocale, changeLocale, type]
  );

  const t = useTranslation();

  return (
    <>
      <Box paddingY="x1_5">
        <ButtonWithDropdown
          data-cy-id="locale-dropdown"
          styleType="passiveLarge"
          floatLeft
          onSelect={handleSelection}
          options={localeOptions}
          secondaryOptions={
            user.data.loggedIn
              ? [
                  {
                    label: t('app.topbar.lang.feedback'),
                    value: 'feedback',
                    icon: <SupportSolid />,
                  },
                ]
              : undefined
          }
        >
          <Grid templateColumns="16px max-content" alignItems="center">
            <Text typography="xSmall" as="span">
              {locale.toUpperCase()}
            </Text>
          </Grid>
        </ButtonWithDropdown>
      </Box>
      {chosenLocale !== '' && (
        <Modal
          title={`${localeNames.get(chosenLocale) ?? 'Your '} language request`}
          onClose={() => setChosenLocale('')}
          primaryLabel="OK"
          onPrimaryClick={() => setChosenLocale('')}
        >
          <p>
            Thank you for your inquiry about the{' '}
            {localeNames.get(chosenLocale) ?? 'translated'} version of Simply
            Wall St. We&apos;ve noted your request and will notify you once it
            becomes available on the platform if there is sufficient demand.
          </p>
        </Modal>
      )}
    </>
  );
};
